import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Space,
  Tag,
  Alert,
  Typography,
  Divider,
  Descriptions,
  Spin,
  message,
} from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';

const { Title, Paragraph } = Typography;

interface DriverOption {
  id: number;
  fullName: string | null;
  phone: string;
  pinfl: string | null;
}

interface CheckResult {
  pinfl: string;
  status: 'REGISTERED' | 'NOT_REGISTERED';
  reason: string;
  data: unknown;
  checkedAt: string | Date;
  // Set when the check was driver-bound (the selected driver's row was
  // updated with the cached status as a side effect).
  driverId?: number;
}

/**
 * Standalone admin page for running an ad-hoc Soliq self-employment check.
 * Two ways to run it:
 *   1. Pick a driver from the dropdown (uses /admin/drivers/:id/self-employment-check
 *      so the result is persisted on the driver's row).
 *   2. Enter a 14-digit PINFL manually (uses /admin/self-employment-check
 *      without persisting anywhere).
 */
export const SelfEmploymentCheckPage = () => {
  const { can } = usePermission();

  const [pinfl, setPinfl] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState<number | undefined>(
    undefined,
  );
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [driverLoading, setDriverLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load drivers when the dropdown is opened or the search query changes.
  // Debounce search to avoid pounding the API on every keystroke.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setDriverLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('limit', '20');
        if (driverSearch.trim()) params.set('search', driverSearch.trim());
        const res = await apiClient.get(`/admin/drivers?${params.toString()}`);
        if (cancelled) return;
        // The API may return { data, meta } or { data: { data, meta } }
        // depending on Refine's data-provider — handle both.
        const list: DriverOption[] =
          res.data?.data?.data ??
          res.data?.data ??
          [];
        setDrivers(list);
      } catch {
        if (!cancelled) setDrivers([]);
      } finally {
        if (!cancelled) setDriverLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [driverSearch]);

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.id === selectedDriverId),
    [drivers, selectedDriverId],
  );

  // When the user picks a driver, pre-fill the PINFL field if the driver
  // already has one. The user can still override it before checking.
  useEffect(() => {
    if (selectedDriver?.pinfl) {
      setPinfl(selectedDriver.pinfl);
    }
  }, [selectedDriver?.pinfl]);

  const handleCheck = async () => {
    setError(null);
    setResult(null);

    const trimmed = pinfl.trim();
    if (!/^\d{14}$/.test(trimmed)) {
      setError('PINFL должен быть ровно 14 цифр');
      return;
    }

    setChecking(true);
    try {
      let res;
      if (selectedDriverId) {
        // Driver-bound: persists the result on the driver's row.
        res = await apiClient.get(
          `/admin/drivers/${selectedDriverId}/self-employment-check`,
        );
      } else {
        // Manual: just runs the check, no persistence.
        res = await apiClient.post('/admin/self-employment-check', {
          pinfl: trimmed,
        });
      }
      const r: CheckResult = res.data;
      setResult(r);
      if (r.status === 'REGISTERED') {
        message.success(`✅ Самозанятый — ${r.reason || "Ma'lumot topildi"}`);
      } else {
        message.warning(
          `❌ Не зарегистрирован — ${r.reason || "Ma'lumot topilmadi"}`,
        );
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Soliq tekshiruvi muvaffaqiyatsiz';
      setError(msg);
      message.error(msg);
    } finally {
      setChecking(false);
    }
  };

  const renderResult = () => {
    if (!result) return null;
    const isRegistered = result.status === 'REGISTERED';
    return (
      <Card
        style={{ marginTop: 24 }}
        bordered
        type="inner"
        title={
          <Space>
            {isRegistered ? (
              <CheckCircleOutlined style={{ color: '#10B981' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#EF4444' }} />
            )}
            <span>Результат проверки</span>
          </Space>
        }
      >
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="PINFL">
            <code>{result.pinfl}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Статус">
            {isRegistered ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Самозанятый
              </Tag>
            ) : (
              <Tag color="red" icon={<CloseCircleOutlined />}>
                Не зарегистрирован
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Reason">
            {result.reason || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Data (raw)">
            {result.data == null ? (
              <span style={{ color: '#95A5A6' }}>null</span>
            ) : typeof result.data === 'string' ? (
              result.data
            ) : (
              <code>{JSON.stringify(result.data)}</code>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Время проверки">
            {new Date(result.checkedAt).toLocaleString('uz-UZ')}
          </Descriptions.Item>
          {result.driverId && (
            <Descriptions.Item label="Сохранено на водителе">
              #{result.driverId}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    );
  };

  if (!can('drivers', 'read')) {
    return (
      <Alert
        type="error"
        message="Нет прав доступа"
        description="Для проверки самозанятости требуется право drivers:read"
      />
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 16 }}>
      <Title level={3}>
        <Space>
          <SafetyCertificateOutlined />
          Проверка самозанятости (Soliq.uz)
        </Space>
      </Title>
      <Paragraph type="secondary">
        Проверьте, зарегистрирован ли человек как самозанятый, по 14-значному
        ПИНФЛ. Можно вручную ввести ПИНФЛ или выбрать водителя из списка —
        тогда результат будет сохранён на его профиле.
      </Paragraph>

      <Card>
        <Form layout="vertical" onFinish={handleCheck}>
          <Form.Item
            label={
              <Space>
                <UserOutlined />
                Выбрать водителя (необязательно)
              </Space>
            }
            help="Если выбрать — PINFL подставится автоматически и результат сохранится на профиле водителя."
          >
            <Select
              showSearch
              allowClear
              placeholder="Найти водителя по имени или телефону"
              value={selectedDriverId}
              onChange={(v) => {
                setSelectedDriverId(v);
                if (!v) {
                  // Don't auto-clear PINFL when deselecting — the staff
                  // may want to keep manual mode with the same number.
                }
              }}
              onSearch={setDriverSearch}
              filterOption={false}
              loading={driverLoading}
              notFoundContent={driverLoading ? <Spin size="small" /> : null}
              style={{ width: '100%' }}
              options={drivers.map((d) => ({
                value: d.id,
                label: `${d.fullName || 'Без имени'} — ${d.phone}${d.pinfl ? ` (PINFL: ${d.pinfl})` : ''}`,
              }))}
            />
          </Form.Item>

          <Divider style={{ margin: '12px 0' }}>или</Divider>

          <Form.Item
            label="ПИНФЛ (14 цифр)"
            required
            validateStatus={
              pinfl && !/^\d{14}$/.test(pinfl.trim()) ? 'error' : ''
            }
            help={
              pinfl && !/^\d{14}$/.test(pinfl.trim())
                ? 'PINFL должен содержать ровно 14 цифр'
                : undefined
            }
          >
            <Input
              size="large"
              placeholder="14-значный ПИНФЛ"
              value={pinfl}
              onChange={(e) =>
                setPinfl(e.target.value.replace(/\D/g, '').slice(0, 14))
              }
              maxLength={14}
              prefix={<SafetyCertificateOutlined />}
              autoFocus
            />
          </Form.Item>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
              closable
              onClose={() => setError(null)}
            />
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              icon={<SafetyCertificateOutlined />}
              loading={checking}
              disabled={!/^\d{14}$/.test(pinfl.trim())}
              block
            >
              Проверить через Soliq.uz
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {renderResult()}
    </div>
  );
};
